import { useResponsive } from "@h/useResponsive";
import { cn } from "@u/cn";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@ui/dialog";
import ResponsiveSheet from "@ui/responsive-sheet";
import { type ReactNode } from "react";

interface ResponsiveModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  /** Custom node rendered in place of the plain title. */
  titleNode?: ReactNode;
  /** Small mono tag above the title. */
  topTag?: string;
  description?: string;
  size?: "default" | "sm" | "md" | "lg" | "xl" | "full";
  design?: "default" | "glass";
  /** Sticky footer (actions). Rendered at the bottom on both layouts. */
  footer?: ReactNode;
  children: ReactNode;
  className?: string;
  /** Extra classes for the desktop DialogContent. */
  contentClassName?: string;
}

/**
 * Canonical modal shell. Encapsulates the responsive branch
 * (mobile → ResponsiveSheet, desktop → Dialog) that was duplicated
 * across 25 modals. Use for the common title + body + footer case;
 * complex modals with bespoke internals can still compose Dialog directly.
 */
export function ResponsiveModal({
  open,
  onOpenChange,
  title,
  titleNode,
  topTag,
  description,
  size = "default",
  design = "glass",
  footer,
  children,
  className,
  contentClassName,
}: ResponsiveModalProps) {
  const { isMobile, isLandscapeMobile } = useResponsive();

  if (isMobile || isLandscapeMobile) {
    return (
      <ResponsiveSheet
        open={open}
        onOpenChange={onOpenChange}
        title={title}
        titleNode={titleNode}
        variant={design}
        className={className}
      >
        <div className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 overflow-y-auto custom-scrollbar pb-4">
            {children}
          </div>
          {footer && (
            <div className="shrink-0 pt-4 border-t border-border/20 flex flex-col-reverse gap-3">
              {footer}
            </div>
          )}
        </div>
      </ResponsiveSheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        size={size}
        design={design}
        scrollable
        className={cn("w-[92vw] sm:w-full", contentClassName)}
      >
        <div className="flex-1 overflow-y-auto px-6 sm:px-10 pt-5 pb-6 sm:pb-10 custom-scrollbar">
          <DialogHeader className="mb-6 pr-12">
            {topTag && (
              <span className="label-micro text-muted-foreground ml-1">
                {topTag}
              </span>
            )}
            <DialogTitle className="text-bubbly text-2xl sm:text-3xl leading-none">
              {titleNode ?? title}
            </DialogTitle>
            <DialogDescription className={cn(!description && "sr-only")}>
              {description ?? title}
            </DialogDescription>
          </DialogHeader>
          {children}
        </div>
        {footer && (
          <div className="shrink-0 px-6 sm:px-10 py-4 border-t border-border/20 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            {footer}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
